import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { Collapse, Typography } from "@mui/material";
import { History, Plus, X } from "lucide-react";

import BaseModal from "@reusables/Modals/BaseModal";
import BaseInput from "@reusables/BaseInput";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";
import { Product, SaleOrder } from "@/types/general";
import { cn, toastError } from "@helpers/utils";
import {
    useCreateProductReservationMutation,
    useDeleteProductReservationMutation,
    useUpdateProductReservationMutation
} from "@redux/features/products/productsApi";
import { useIsLocationReservationsEnabled } from "@redux/features/companies/useIsReservationsEnabled";
import { isErrorWithMessage } from "@redux/api/query";
import UIButton from "@reusables/UIButton";
import BaseBadge from "@reusables/BaseBadge";
import BaseTooltip from "@reusables/BaseTooltip";
import ReservationLocationCommandDropdown, {
    getLocationKey,
    listReservationLocations
} from "@ready/ReservationLocationCommandDropdown";

interface ReservationModalProps {
    isOpen: boolean;
    onClose: () => void;
    product: Product.Extended;
    order?: SaleOrder.Root;
    reservation?: Product.Augmentations.Reservation.Root;
}

/**
 * Location label of a reservation, or null for a global one.
 */
export function getReservationLocationLabel(reservation: Product.Augmentations.Reservation.Item): string | null {
    if (!reservation.store) return null;

    return reservation.section ? `${reservation.store.name} - ${reservation.section.name}` : reservation.store.name;
}

const rowSchema = z.object({
    // Existing reservation. Only its quantity can be changed here - moving it to another location
    // is done in the sale order form, where the whole split is visible.
    reservation_id: z.number().optional(),
    // null -> global reservation
    location: z
        .object({
            store_id: z.number(),
            section_id: z.number().nullable()
        })
        .nullable(),
    // 0 cancels an existing reservation, and skips a new one
    quantity: z.coerce.number().min(0)
});

// Location | quantity | remove
const ROW_COLUMNS = "grid grid-cols-[minmax(0,1fr)_180px_32px] gap-x-3";

const formSchema = z.object({
    rows: z.array(rowSchema)
});

type FormTyping = z.infer<typeof formSchema>;

export default function ReservationModal({ isOpen, onClose, product, order, reservation }: ReservationModalProps) {
    const { t } = useTranslation("", { keyPrefix: "products.viewEditProduct.tabs.saleOrder.modals.reservation" });

    const isLocationReservationsEnabled = useIsLocationReservationsEnabled();

    const [createReservation] = useCreateProductReservationMutation();
    const [updateReservation] = useUpdateProductReservationMutation();
    const [deleteReservation] = useDeleteProductReservationMutation();

    // Requests are sent one by one, so the loading state spans the whole sequence
    const [isSaving, setIsSaving] = useState(false);

    const existingReservations = useMemo(() => reservation?.reservations ?? [], [reservation]);
    const isEditing = existingReservations.length > 0;

    const existingById = useMemo(
        () => new Map(existingReservations.map((item) => [item.id, item])),
        [existingReservations]
    );

    const getExisting = (id: number) => {
        const item = existingById.get(id);

        if (!item) {
            throw new Error(`Reservation #${id} is not among the reservations the modal was opened with`);
        }

        return item;
    };

    // The limit is the same on every entry. Orders without reservations don't provide one,
    // so the backend stays the only check for them (as before).
    const limit = existingReservations[0]?.limit;

    const { control, handleSubmit, reset } = useForm<FormTyping>({
        resolver: zodResolver(formSchema),
        defaultValues: { rows: [] }
    });

    const { fields, append, remove } = useFieldArray({ control, name: "rows" });

    const rows = useWatch({ control, name: "rows" });

    useEffect(() => {
        if (!isOpen) return;

        reset({
            rows:
                existingReservations.length > 0
                    ? existingReservations.map((item) => ({
                          reservation_id: item.id,
                          location: item.store
                              ? { store_id: item.store.id, section_id: item.section?.id ?? null }
                              : null,
                          quantity: item.quantity
                      }))
                    : [{ location: null, quantity: 0 }]
        });
    }, [isOpen, existingReservations, reset]);

    const productLocations = useMemo(() => product.locations ?? [], [product.locations]);

    const usedLocationKeys = rows.map((row) => getLocationKey(row.location));
    // In the order the picker lists them, so a new row gets the first location the user would see
    const freeLocations = listReservationLocations(productLocations).filter(
        (location) => !usedLocationKeys.includes(getLocationKey(location))
    );

    const total = rows.reduce((acc, row) => acc + (Number(row.quantity) || 0), 0);
    const exceedsLimit = limit !== undefined && total > limit;

    // Index of a row added through "Add location", whose location picker opens right away
    const [autoOpenIndex, setAutoOpenIndex] = useState<number | null>(null);

    // Why "Add location" can't be used right now, if it can't
    const addLocationBlocker = !freeLocations.length
        ? t("buttons.allLocationsUsed")
        : limit !== undefined && total >= limit
        ? t("buttons.nothingLeftToReserve")
        : null;

    const handleAddLocation = () => {
        if (addLocationBlocker) {
            throw new Error(`A location can't be added for product #${product.id}: ${addLocationBlocker}`);
        }

        setAutoOpenIndex(fields.length);
        append({
            location: freeLocations[0],
            // Without a known limit the backend is the only check, so nothing is prefilled
            quantity: limit !== undefined ? Math.max(limit - total, 0) : 0
        });
    };

    const onSubmit = handleSubmit(async (data) => {
        if (!order) {
            throw new Error("Reservation modal was submitted without an order");
        }

        if (exceedsLimit) return;

        const keptIds = new Set(data.rows.flatMap((row) => (row.reservation_id ? [row.reservation_id] : [])));

        const cancellations = [
            ...existingReservations.filter((item) => !keptIds.has(item.id)).map((item) => item.id),
            ...data.rows.flatMap((row) => (row.reservation_id && row.quantity === 0 ? [row.reservation_id] : []))
        ];

        // Decreases go before increases, so the reserved total never exceeds the limit in between requests
        const updates = data.rows
            .flatMap((row) => {
                if (!row.reservation_id || row.quantity === 0) return [];

                const delta = row.quantity - getExisting(row.reservation_id).quantity;
                return delta === 0 ? [] : [{ reservation_id: row.reservation_id, quantity: row.quantity, delta }];
            })
            .sort((a, b) => a.delta - b.delta);

        const creations = data.rows.filter((row) => !row.reservation_id && row.quantity > 0);

        if (!cancellations.length && !updates.length && !creations.length) {
            onClose();
            return;
        }

        setIsSaving(true);

        try {
            // Sequential on purpose: the backend checks each request against the reservations that already exist
            for (const reservationId of cancellations) {
                await deleteReservation({ product_id: product.id, reservation_id: reservationId }).unwrap();
            }

            for (const update of updates) {
                await updateReservation({
                    product_id: product.id,
                    reservation_id: update.reservation_id,
                    quantity: update.quantity
                }).unwrap();
            }

            for (const row of creations) {
                await createReservation({
                    product_id: product.id,
                    sale_order_id: order.id,
                    ...(row.location === null
                        ? { quantity: row.quantity }
                        : { locations: [{ ...row.location, quantity: row.quantity }] })
                }).unwrap();
            }

            toast.success(t("responses.success"));
            onClose();
        } catch (e) {
            // Requests sent before the failed one are already applied, the refetched list shows the actual state
            if (isErrorWithMessage(e)) {
                toastError(e);
            } else {
                console.error("An error occurred while saving product reservations:", e);
            }
        } finally {
            setIsSaving(false);
        }
    });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={962}
            padding="56px"
            isLoading={isSaving}
            useCloseIcon
        >
            <form className="space-y-[32px]" onSubmit={onSubmit}>
                <div className="text-xl font-semibold text-center text-accent">
                    {isEditing ? t("heading.editing") : t("heading.creation")}
                </div>

                <div className="space-y-2">
                    {/* SO code */}
                    <div className="text-accent">
                        {t("subheading.order")}: <BaseBadge>{order?.code}</BaseBadge>
                    </div>

                    {/* Product name */}
                    <div className="text-accent">
                        {t("subheading.product")}: <span className="font-semibold">{product.name}</span>
                    </div>
                </div>

                <div className="space-y-3">
                    {/* Column headers, shared by all rows so the fields line up */}
                    <div className={cn(ROW_COLUMNS, "text-inputs-label-dim")}>
                        <div>{t("fields.location.label")}</div>
                        <div>{t("fields.quantity.label")}</div>
                    </div>

                    {fields.map((field, index) => {
                        const row = rows[index];
                        // Not throwing here: the rows are reset right after the reservations are refetched
                        const existing = row?.reservation_id ? existingById.get(row.reservation_id) : undefined;

                        const hasChanged = !!existing && Number(row.quantity) !== existing.quantity;

                        return (
                            <div key={field.id} className={cn(ROW_COLUMNS, "items-center")}>
                                {/* Location */}
                                <div className="min-w-0">
                                    {!existing && isLocationReservationsEnabled ? (
                                        <Controller
                                            name={`rows.${index}.location`}
                                            control={control}
                                            render={({ field: locationField }) => (
                                                <ReservationLocationCommandDropdown
                                                    locations={productLocations}
                                                    value={locationField.value}
                                                    onChange={locationField.onChange}
                                                    excludedKeys={
                                                        new Set(
                                                            usedLocationKeys.filter(
                                                                (_, keyIndex) => keyIndex !== index
                                                            )
                                                        )
                                                    }
                                                    autoOpen={autoOpenIndex === index}
                                                    onAutoOpenHandled={() => setAutoOpenIndex(null)}
                                                />
                                            )}
                                        />
                                    ) : (
                                        <div className="text-accent break-words">
                                            {(existing && getReservationLocationLabel(existing)) ??
                                                t("anyLocation")}
                                        </div>
                                    )}
                                </div>

                                {/* Quantity */}
                                <Controller
                                    name={`rows.${index}.quantity`}
                                    control={control}
                                    render={({ field: quantityField, fieldState }) => (
                                        <BaseInput
                                            type="number"
                                            value={quantityField.value}
                                            onChange={(val) => quantityField.onChange(val)}
                                            error={fieldState.error}
                                            hideArrows
                                        />
                                    )}
                                />

                                {/* Remove */}
                                <div className="flex justify-center">
                                    {fields.length > 1 && (
                                        <BaseMaterialIconButton
                                            type="button"
                                            style="dim"
                                            size="small"
                                            icon={<X size={16} />}
                                            tip={t("buttons.removeLocation")}
                                            onClick={() => remove(index)}
                                        />
                                    )}
                                </div>

                                {/* Previous value, kept under the quantity field */}
                                <div className="col-start-2">
                                    <Collapse in={hasChanged}>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            sx={{ opacity: 0.5 }}
                                            className="flex items-center space-x-2 pt-2"
                                        >
                                            <History size={12} />
                                            <span>{t("previousValue", { value: existing?.quantity })}</span>
                                        </Typography>
                                    </Collapse>
                                </div>
                            </div>
                        );
                    })}

                    <div className={cn(ROW_COLUMNS, "items-center pt-1")}>
                        <div>
                            {isLocationReservationsEnabled && (
                                <BaseTooltip
                                    title={addLocationBlocker as string}
                                    active={!!addLocationBlocker}
                                    placement="top"
                                >
                                    <UIButton
                                        // Inside the form a button submits it unless told otherwise
                                        type="button"
                                        size={"sm"}
                                        variant={"outline"}
                                        disabled={!!addLocationBlocker}
                                        onClick={handleAddLocation}
                                    >
                                        <Plus size={14} />
                                        <span>{t("buttons.addLocation")}</span>
                                    </UIButton>
                                </BaseTooltip>
                            )}
                        </div>

                        {/* Total, under the quantity column */}
                        <div
                            className={cn(
                                "text-sm pl-4 whitespace-nowrap",
                                exceedsLimit ? "text-[#E25C5C]" : "text-gray-500"
                            )}
                        >
                            {limit !== undefined && t("total", { total, limit })}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-[16px]">
                    <UIButton type="button" size={"lg"} variant={"outline"} onClick={onClose}>
                        {t("buttons.cancel")}
                    </UIButton>
                    <UIButton size={"lg"} type="submit" loading={isSaving} disabled={exceedsLimit}>
                        {t("buttons.reserve")}
                    </UIButton>
                </div>
            </form>
        </BaseModal>
    );
}
